import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const WoodEngineering: React.FC = () => {
  const woodSolutions = [
    {
      title: "Precision Cutting Systems",
      description: "Advanced wood cutting and processing equipment for precise manufacturing.",
      icon: "🪚",
      features: ["CNC cutting machines", "Laser cutting systems", "Precision saws"]
    },
    {
      title: "Automated Woodworking",
      description: "Automated woodworking systems for efficient production and consistent quality.",
      icon: "🏭",
      features: ["Automated assembly lines", "Quality control systems", "Production optimization"]
    },
    {
      title: "Sustainable Timber Processing",
      description: "Eco-friendly timber processing solutions that maximize resource efficiency.",
      icon: "🌲",
      features: ["Waste reduction systems", "Recycling processes", "Sustainable sourcing"]
    },
    {
      title: "Custom Wood Products",
      description: "Custom wood product manufacturing for furniture, construction, and decorative items.",
      icon: "🪑",
      features: ["Custom furniture", "Architectural elements", "Decorative pieces"]
    }
  ];

  const services = [
    "Custom wood processing equipment design",
    "Automated woodworking systems",
    "Timber processing and treatment",
    "Furniture manufacturing",
    "Quality control and testing",
    "Maintenance and repair services"
  ];

  const benefits = [
    {
      title: "Efficiency",
      description: "Automated systems that increase production efficiency by up to 400%",
      icon: "⚡"
    },
    {
      title: "Precision",
      description: "Advanced cutting and processing for perfect precision and quality",
      icon: "🎯"
    },
    {
      title: "Sustainability",
      description: "Eco-friendly processes that minimize waste and environmental impact",
      icon: "🌱"
    },
    {
      title: "Customization",
      description: "Flexible systems that adapt to various wood processing needs",
      icon: "🔧"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-amber-600 to-amber-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-tool-swing">
                <EngineeringIcon type="hammer" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">Wood Works Engineering</h1>
            <p className="text-xl mb-8">
              Advanced wood processing and manufacturing solutions. From precision cutting to automated woodworking 
              systems and sustainable timber processing.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Precision Engineering</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Sustainable Processing</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Custom Solutions</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Wood Solutions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Wood Processing Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive wood engineering solutions designed for efficiency, precision, and sustainability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {woodSolutions.map((solution, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-pulley-bounce">{solution.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{solution.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-amber-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Our Wood Engineering?</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our wood processing solutions deliver exceptional benefits for your manufacturing operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-amber-50 transition-colors">
                <div className="text-4xl mb-4 animate-bounce-slow">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive wood engineering services to meet all your wood processing and manufacturing needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <EngineeringIcon type="wrench" size="md" className="text-amber-500 mr-4" animated={false} />
                  <span className="font-semibold text-gray-800">{service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-amber-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Transform Your Wood Processing</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Experience the future of wood engineering with our advanced processing solutions. 
            Let's build efficient, sustainable, and precise wood manufacturing systems.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-amber-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="hammer" size="sm" animated={false} />
              Get a Quote
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-amber-600 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" />
              View Our Equipment
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WoodEngineering; 